import { type FC } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import moment from "moment";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useStreamingUsageQuery } from "@/partner/entities/streamingUsage";
import {
  useCurrentPlatformQuery,
  useIsBunnyProvider,
} from "@/partner/entities/platforms";
import { formatGb, formatUsdCost } from "@/partner/entities/storage";
import { formatNumber } from "@/shared/utils/formatNumber";
import { PagePaths } from "@/partner/shared/constants";
import {
  UsageAlertKindType,
  UsageAlertSeverityType,
} from "@/types/graphql/partner.generated";

const formatUsd = (value: number) => `$${value.toFixed(2)}`;

// CRITICAL-RULES.md — i18n partner-кабинета: ключ i18n только статический
// литерал; выбор текста по значению enum — через явную мапу (см.
// StorageTotals.tsx), не через шаблонную строку с интерполяцией enum.
const ALERT_TEXT_KEYS: Record<UsageAlertKindType, string> = {
  [UsageAlertKindType.StorageNearLimitType]: "usage.alert.storageNearLimit",
  [UsageAlertKindType.ViewsSpikeType]: "usage.alert.viewsSpike",
};

// Плитки без hover-подсветки: раньше блок хранилища был CardActionArea с
// отрицательными полями и подсвечивался целиком, наезжая на соседний текст.
const TILE_SX = {
  p: 2,
  border: 1,
  borderColor: "divider",
  borderRadius: 2,
} as const;

const ALERT_SEVERITY: Record<UsageAlertSeverityType, "warning" | "error"> = {
  [UsageAlertSeverityType.WarningType]: "warning",
  [UsageAlertSeverityType.CriticalType]: "error",
};

/**
 * Тексты шапки и пустых состояний у провайдеров свои: у Cloudflare это
 * подписка с лимитом минут, у Bunny оплата по факту за гигабайты. Ключи
 * собраны в мапу по тому же правилу, что ALERT_TEXT_KEYS выше.
 */
const CARD_TEXT_KEYS = {
  cloudflare: {
    title: "streamingUsage.title",
    subheader: "streamingUsage.subheader",
    notConnected: "streamingUsage.notConnected",
    error: "streamingUsage.error",
  },
  bunny: {
    title: "streamingUsage.bunny.title",
    subheader: "streamingUsage.bunny.subheader",
    notConnected: "streamingUsage.bunny.notConnected",
    error: "streamingUsage.bunny.error",
  },
} as const;

/**
 * Страница Cloudflare, где докупают минуты хранения (Media → Manage plan). С
 * известным accountId ссылка ведёт прямо в нужный аккаунт, без него
 * dash.cloudflare.com сам предложит выбрать аккаунт через `?to=`.
 */
function cloudflareStreamPlansUrl(accountId?: string | null): string {
  return accountId
    ? `https://dash.cloudflare.com/${accountId}/media/manage-plan`
    : "https://dash.cloudflare.com/?to=/:account/media/manage-plan";
}

export const StreamingUsageCard: FC = () => {
  const { t, i18n } = useTranslation();
  const isBunny = useIsBunnyProvider();
  const { data: platformData } = useCurrentPlatformQuery();
  const cloudflareAccountId =
    platformData?.currentPlatform?.streamingSettings?.accountId;
  const cardTexts = isBunny ? CARD_TEXT_KEYS.bunny : CARD_TEXT_KEYS.cloudflare;
  const { data, error } = useStreamingUsageQuery();
  const usage = data?.streamingUsage;

  if (!usage && !error) return null;

  let content;
  if (error || !usage) {
    content = (
      <Typography color="text.secondary">{t(cardTexts.error)}</Typography>
    );
  } else if (!usage.connected) {
    content = (
      <>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {t(cardTexts.notConnected)}
        </Typography>
        <Button
          variant="outlined"
          component={Link}
          to={PagePaths.platformSettings}
        >
          {t("streamingUsage.configure")}
        </Button>
      </>
    );
  } else {
    const storage = usage.storage;
    const usedPercent =
      storage && storage.limitMinutes > 0
        ? Math.min(100, (storage.usedMinutes / storage.limitMinutes) * 100)
        : 0;
    const period =
      usage.periodStart && usage.periodEnd
        ? // Явная локаль на инстансе: набор форматов interpolationFormat
          // зеркалит site и «день + короткий месяц» без года в нём нет.
          `${moment(usage.periodStart)
            .locale(i18n.language)
            .format("D MMM")} - ${moment(usage.periodEnd)
            .locale(i18n.language)
            .format("D MMM")}`
        : "";
    const frequencySuffix = usage.subscription
      ? t(`streamingUsage.frequency.${usage.subscription.frequency}`, {
          defaultValue: "",
        })
      : "";

    content = (
      <>
        {(usage.alerts ?? []).map((alert) => (
          <Alert
            key={alert.kind}
            severity={ALERT_SEVERITY[alert.severity]}
            sx={{ mb: 2 }}
            action={
              // Лимит минут есть только у Cloudflare: алерт про хранилище
              // ведёт сразу туда, где квоту докупают.
              alert.kind === UsageAlertKindType.StorageNearLimitType &&
              !isBunny ? (
                <Button
                  color="inherit"
                  size="small"
                  href={cloudflareStreamPlansUrl(cloudflareAccountId)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("usage.alert.storageNearLimitAction")}
                </Button>
              ) : undefined
            }
          >
            {t(ALERT_TEXT_KEYS[alert.kind], {
              percent: Math.round(alert.value * 100),
              minutes: formatNumber(Math.round(alert.value)),
            })}
          </Alert>
        ))}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          }}
        >
          {storage ? (
            <Box sx={{ ...TILE_SX, gridColumn: { md: "1 / -1" } }}>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("streamingUsage.storage.label")}
                  </Typography>
                  <Button
                    component={Link}
                    to={PagePaths.storage}
                    size="small"
                    endIcon={<ArrowForwardIcon fontSize="small" />}
                    sx={{ mr: -1 }}
                  >
                    {t("menu.storage")}
                  </Button>
                </Box>
                {isBunny ? (
                  // Лимита у библиотеки Bunny нет, поэтому ни прогресс-бара,
                  // ни «осталось»: только занятый объём.
                  <Typography variant="body1" sx={{ my: 1 }}>
                    {t("streamingUsage.bunny.storage.usage", {
                      size: formatGb(storage.usedBytes ?? 0),
                    })}
                  </Typography>
                ) : (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={usedPercent}
                      sx={{ my: 1 }}
                    />
                    <Typography variant="body2">
                      {t("streamingUsage.storage.usage", {
                        used: formatNumber(Math.round(storage.usedMinutes)),
                        limit: formatNumber(storage.limitMinutes),
                        left: formatNumber(
                          Math.max(
                            0,
                            Math.round(
                              storage.limitMinutes - storage.usedMinutes
                            )
                          )
                        ),
                      })}
                    </Typography>
                  </>
                )}
                <Typography variant="body2" color="text.secondary">
                  {t("streamingUsage.storage.videos", {
                    count: storage.videoCount,
                  })}
                </Typography>
              </Box>
            </Box>
          ) : (
            // Библиотека не ответила (нет аккаунтного ключа bunny.net либо
            // сбой core API): молчать нельзя, иначе пропавший блок читается
            // как «хранение пустое».
            isBunny && (
              <Box sx={{ ...TILE_SX, gridColumn: { md: "1 / -1" } }}>
                <Typography variant="body2" color="text.secondary">
                  {t("streamingUsage.bunny.storage.unavailable")}
                </Typography>
              </Box>
            )
          )}

          <Box sx={TILE_SX}>
            <Typography variant="subtitle2" color="text.secondary">
              {t("streamingUsage.viewed.label", { period })}
            </Typography>
            {/* Подсказки про права токена относятся к Cloudflare: у Bunny
              прав API-ключа партнёр не настраивает. */}
            {!isBunny && usage.analyticsScopeMissing ? (
              <Typography variant="body2" color="text.secondary">
                {t("streamingUsage.analyticsScopeMissing")}
              </Typography>
            ) : isBunny && usage.viewedMinutes == null ? (
              <Typography variant="body1">{t("common.emptyDash")}</Typography>
            ) : (
              <Typography variant="body1">
                {t("streamingUsage.viewed.value", {
                  minutes: formatNumber(Math.round(usage.viewedMinutes ?? 0)),
                })}
              </Typography>
            )}
          </Box>

          <Box sx={TILE_SX}>
            <Typography variant="subtitle2" color="text.secondary">
              {t("streamingUsage.cost.label")}
            </Typography>
            {isBunny ? (
              // Подписки у Bunny нет: платят по факту за хранение и трафик.
              <>
                {usage.cost?.storage == null ? (
                  // Без объёма библиотеки не считается ни хранение, ни трафик:
                  // $0.00 здесь означал бы «бесплатно», а не «неизвестно».
                  <Typography variant="body2" color="text.secondary">
                    {t("streamingUsage.bunny.cost.unavailable")}
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    {t("streamingUsage.bunny.cost.storage", {
                      amount: formatUsdCost(usage.cost.storage),
                    })}
                  </Typography>
                )}
                {usage.cost?.delivery != null && (
                  <Typography variant="body2">
                    {t("streamingUsage.cost.delivery", {
                      amount: formatUsdCost(usage.cost.delivery),
                    })}
                  </Typography>
                )}
                {usage.cost?.total != null && (
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {t("streamingUsage.cost.total", {
                      amount: formatUsdCost(usage.cost.total),
                    })}
                  </Typography>
                )}
                {usage.cost?.storage != null && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {t("streamingUsage.bunny.cost.note")}
                  </Typography>
                )}
              </>
            ) : (
              <>
                {usage.billingScopeMissing ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("streamingUsage.billingScopeMissing")}
                  </Typography>
                ) : (
                  usage.subscription && (
                    <Typography variant="body2">
                      {t("streamingUsage.cost.subscription", {
                        amount: `${usage.subscription.price.toFixed(2)} ${
                          usage.subscription.currency
                        }`,
                        frequency: frequencySuffix,
                      })}
                    </Typography>
                  )
                )}
                {usage.cost?.delivery != null && (
                  <Typography variant="body2">
                    {t("streamingUsage.cost.delivery", {
                      amount: formatUsd(usage.cost.delivery),
                    })}
                  </Typography>
                )}
                {usage.cost?.total != null && (
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {t("streamingUsage.cost.total", {
                      amount: formatUsd(usage.cost.total),
                    })}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </>
    );
  }

  return (
    <Card>
      <CardHeader
        title={t(cardTexts.title)}
        subheader={t(cardTexts.subheader)}
      />
      <CardContent sx={{ pt: 0 }}>{content}</CardContent>
    </Card>
  );
};
